import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { MapPinIcon } from "@heroicons/react/24/solid";
import { GOOGLE_MAPS_API_KEY } from "Constants";
import { GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_LOADER_ID } from "Pages/Redesign/api";
import { GhostButton } from "../../Pages/Sponsor/adminShared";

// server/app/consts/map.go の DefaultMapLat/Lng と同じ (渋谷周辺)
const DEFAULT_CENTER = { lat: 35.650547, lng: 139.704221 };

const round6 = (n: number) => Math.round(n * 1e6) / 1e6;

const HubLocationPicker = ({ register, watch, setValue, getValues, errors, setError }: {
  register: any, watch: any, setValue: any, getValues: any, errors: any, setError: any,
}) => {
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_LOADER_ID,
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const lat = Number(watch("lat"));
  const lng = Number(watch("lng"));
  const hasPosition = Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);

  const setPosition = (newLat: number, newLng: number) => {
    setValue("lat", round6(newLat), { shouldValidate: true });
    setValue("lng", round6(newLng), { shouldValidate: true });
  };

  const handleGeocodeFromAddress = () => {
    if (!isLoaded || !window.google || !window.google.maps) {
      setError("地図を読み込み中です。少し待ってから再度お試しください。");
      return;
    }
    const address = [
      getValues("state"),
      getValues("city"),
      getValues("address_line_1"),
      getValues("address_line_2"),
    ].filter(Boolean).join(" ");
    if (!address) {
      setError("住所を入力してから検索してください。");
      return;
    }
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: address }, (results, status) => {
      if (status === "OK" && results != null && results[0]) {
        const location = results[0].geometry.location;
        setPosition(location.lat(), location.lng());
        setError("");
      } else {
        setError("住所から位置を特定できませんでした。地図をクリックして位置を指定してください。");
      }
    });
  };

  const coordinateInputClass = "h-11 px-3 focus:ring-[#FF4D2D] focus:border-[#FF4D2D] block w-full sm:text-sm border-gray-200 rounded-xl";

  return (
    <div className="col-span-6">
      <div className="flex items-center" style={{ marginBottom: 6 }}>
        <label className="block text-sm font-bold text-gray-800">位置情報</label>
        <GhostButton
          type="button"
          onClick={handleGeocodeFromAddress}
          style={{ marginLeft: "auto", padding: "6px 14px", fontSize: 13 }}
        >
          住所から検索
        </GhostButton>
      </div>
      <p className="text-xs text-gray-500" style={{ marginBottom: 8 }}>
        「住所から検索」を押すか、地図をクリック / ピンをドラッグして正確な位置を設定できます。
      </p>
      {GOOGLE_MAPS_API_KEY && isLoaded ? (
        <div style={{ height: 280, borderRadius: 12, overflow: "hidden" }}>
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={hasPosition ? { lat, lng } : DEFAULT_CENTER}
            zoom={hasPosition ? 17 : 12}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              clickableIcons: false,
            }}
            onClick={(e) => {
              if (e.latLng) setPosition(e.latLng.lat(), e.latLng.lng());
            }}
          >
            {hasPosition && (
              <Marker
                position={{ lat, lng }}
                draggable
                onDragEnd={(e) => {
                  if (e.latLng) setPosition(e.latLng.lat(), e.latLng.lng());
                }}
              />
            )}
          </GoogleMap>
        </div>
      ) : (
        <div style={{ height: 280, borderRadius: 12, background: "#F2EDE5" }} />
      )}
      <div className="grid grid-cols-2 gap-6" style={{ marginTop: 12 }}>
        <div>
          <div className="flex items-center" style={{ marginBottom: 6 }}>
            <MapPinIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
            <label htmlFor="lat" className="block text-sm font-bold text-gray-800 ml-1">緯度</label>
          </div>
          <input
            className={coordinateInputClass}
            {...register("lat", {
              valueAsNumber: true,
              validate: (v: number) => isNaN(v) || (v >= -90 && v <= 90) || "緯度は-90〜90の範囲で入力してください",
            })}
            type="number"
            step="any"
            id="lat"
            placeholder="例) 35.648100"
          />
          {errors.lat && <p className="text-red-600 text-xs">{errors.lat.message}</p>}
        </div>
        <div>
          <div className="flex items-center" style={{ marginBottom: 6 }}>
            <MapPinIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
            <label htmlFor="lng" className="block text-sm font-bold text-gray-800 ml-1">経度</label>
          </div>
          <input
            className={coordinateInputClass}
            {...register("lng", {
              valueAsNumber: true,
              validate: (v: number) => isNaN(v) || (v >= -180 && v <= 180) || "経度は-180〜180の範囲で入力してください",
            })}
            type="number"
            step="any"
            id="lng"
            placeholder="例) 139.703100"
          />
          {errors.lng && <p className="text-red-600 text-xs">{errors.lng.message}</p>}
        </div>
      </div>
    </div>
  );
};

export default HubLocationPicker;
